import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { AutomaticOfferProgramDialogProps } from './AutomaticOfferProgramDialog.types';
import { useGenerateOfferProgramMutation } from '../Service/extendedOfferProgramApi';
import { OfferProgramBlockDto } from '../Types/offerProgramTypes';
import { OffersRoutes } from '../../../Router/path';

const AutomaticOfferProgramDialog = ({
  offerId,
  open,
  onClose,
  onSuccess,
}: AutomaticOfferProgramDialogProps) => {
  const [generateOfferProgram, { isLoading, isError, error }] = useGenerateOfferProgramMutation();
  const [totalDays, setTotalDays] = useState(7);
  const [blocks, setBlocks] = useState<OfferProgramBlockDto[]>([]);

  const [blockFrom, setBlockFrom] = useState<number>(1);
  const [blockTo, setBlockTo] = useState<number>(1);
  const [blockDescription, setBlockDescription] = useState<string>('');
  const navigate = useNavigate();

  const handleAddBlock = () => {
    if (
      blockFrom &&
      blockTo &&
      blockDescription &&
      blockFrom <= blockTo &&
      blockFrom >= 1 &&
      blockTo <= totalDays
    ) {
      setBlocks([
        ...blocks,
        {
          id: Date.now() + Math.random(),
          dayFrom: blockFrom,
          dayTo: blockTo,
          description: blockDescription,
        },
      ]);
      setBlockFrom(blockTo + 1);
      setBlockTo(blockTo + 1);
      setBlockDescription('');
    }
  };
  const handleGenerateOfferProgram = async () => {
    await generateOfferProgram({
      totalDays,
      blocks,
      offerId,
    }).unwrap();
    onClose();
    if (onSuccess) onSuccess();
    else navigate(OffersRoutes.root);
  };
  const handleClose = () => {
    onClose();
    if (onSuccess) onSuccess();
    else navigate(OffersRoutes.root);
  };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Automatyczne generowanie programu oferty</DialogTitle>
      <DialogContent>
        <Box mb={2} mt={1}>
          <TextField
            label="Ilość dni"
            type="number"
            value={totalDays}
            onChange={(e) => setTotalDays(Number(e.target.value))}
            fullWidth
            inputProps={{ min: 1, max: 30 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Typography variant="subtitle2" mb={1}>
          Opcjonalnie: Dodaj grupę dni jako blok
        </Typography>
        <Box display="flex" gap={1} mb={2}>
          <TextField
            label="Od dnia"
            type="number"
            value={blockFrom}
            onChange={(e) => setBlockFrom(Number(e.target.value))}
            inputProps={{ min: 1, max: totalDays }}
            size="small"
          />
          <TextField
            label="Do dnia"
            type="number"
            value={blockTo}
            onChange={(e) => setBlockTo(Number(e.target.value))}
            inputProps={{ min: blockFrom, max: totalDays }}
            size="small"
          />
          <TextField
            label="Opis bloku"
            value={blockDescription}
            onChange={(e) => setBlockDescription(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleAddBlock}
            disabled={!blockDescription || blockFrom > blockTo}
          >
            Dodaj blok
          </Button>
        </Box>

        {blocks.length > 0 && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Dodane bloki:
            </Typography>
            {blocks.map((block, idx) => (
              <Typography key={idx} variant="body2">
                {block.dayFrom} - {block.dayTo}: {block.description}
              </Typography>
            ))}
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(error as any)?.data?.message || 'Nie udało się wygenerować programu.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Anuluj
        </Button>
        <Button
          onClick={handleGenerateOfferProgram}
          variant="contained"
          color="primary"
          disabled={isLoading}
        >
          Wygeneruj program
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutomaticOfferProgramDialog;
